import { Component, OnInit, OnChanges, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FileUploadModule } from 'primeng/fileupload';
import { CalendarModule } from 'primeng/calendar';
import { ChipsModule } from 'primeng/chips';

// NgxFileDrop for file upload
import { NgxFileDropModule, NgxFileDropEntry } from 'ngx-file-drop';

// Models and services
import { 
  ProductResponse, 
  CreateProductRequest, 
  UpdateProductRequest,
} from '../../models/product.model';

import { ProductService,
  ProductCategoryService,
  BrandService} from '../../services';
import { ToastMessageService, TaxGroupService } from '@fitness-force/api';

// Enums
import { OwnershipLevel } from '@fitness-force/enum';
import { BehaviorSubject } from 'rxjs';
import { ImageCropDialogComponent } from '@fitness-force/common/crop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// Attribute Schema Types
interface AttributeSchema {
  name: string;
  type: 'enum' | 'boolean' | 'string' | 'number' | 'date';
  required: boolean;
  allowed_values?: string[];
  label?: string;
  display_order: number;
}

@Component({
  selector: 'lib-product-form',
  standalone: true,
  providers: [DialogService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleButtonModule,
    FileUploadModule,
    NgxFileDropModule,
    CalendarModule,
    ChipsModule
  ],
  template: `
    <p-dialog 
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '600px' }"
      (onHide)="onCancel.emit()">
      
      <ng-template pTemplate="header">
        <h3>{{ isEditMode ? ('RETAIL_BILLING.PRODUCTS.EDIT' | translate) : ('RETAIL_BILLING.PRODUCTS.CREATE' | translate) }}</h3>
      </ng-template>


      <!-- Level Selection Section -->
      <div *ngIf="!isEditMode" class="mb-4 p-3 surface-100 border-round">
        <div class="text-900 font-medium mb-2">{{ 'RETAIL_BILLING.PRODUCTS.WHO_IS_THIS_FOR' | translate }}</div>
        <div class="flex gap-2">
          <p-button
            [label]="'RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate"
            [outlined]="productForm.get('ownership_level')?.value !== ownershipLevel.Company"
            [text]="productForm.get('ownership_level')?.value !== ownershipLevel.Company"
            (onClick)="selectLevel(ownershipLevel.Company)"
            [class]="productForm.get('ownership_level')?.value === ownershipLevel.Company ? 'p-button-primary' : ''"
            aria-label="company-level-button">
          </p-button>
          <p-button
            [label]="'RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate"
            [outlined]="productForm.get('ownership_level')?.value !== ownershipLevel.Tenant"
            [text]="productForm.get('ownership_level')?.value !== ownershipLevel.Tenant"
            (onClick)="selectLevel(ownershipLevel.Tenant)"
            [class]="productForm.get('ownership_level')?.value === ownershipLevel.Tenant ? 'p-button-primary' : ''"
            aria-label="current-tenant-button">
          </p-button>
        </div>
      </div>

      <form [formGroup]="productForm" class="grid">

       <!-- Category -->
       <div class="col-12 md:col-6">
          <label for="category">{{ 'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate }}</label>
          <p-select
            id="category"
            formControlName="category_id"
            [options]="categoryOptions"
            optionLabel="name"
            optionValue="value"
            [placeholder]="'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate"
            class="w-full mt-2"
            aria-label="product-category-dropdown">
          </p-select>
          <small class="p-error" *ngIf="productForm.get('category_id')?.invalid && productForm.get('category_id')?.touched">
            {{ 'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate }} is required
          </small>
        </div>

        <!-- Brand -->
        <div class="col-12 md:col-6">
          <label for="brand">{{ 'RETAIL_BILLING.PRODUCTS.BRAND' | translate }}</label>
          <p-select
            id="brand"
            formControlName="brand_id"
            [options]="brandOptions"
            optionLabel="name"
            optionValue="value"
            [placeholder]="'RETAIL_BILLING.PRODUCTS.BRAND' | translate"
            class="w-full mt-2"
            aria-label="product-brand-dropdown">
          </p-select>
          <small class="p-error" *ngIf="productForm.get('brand_id')?.invalid && productForm.get('brand_id')?.touched">
            {{ 'RETAIL_BILLING.PRODUCTS.BRAND' | translate }} is required
          </small>
        </div>

        
        
        <!-- Product Name (Primary Language) -->
        <div class="col-12 md:col-6">
          <label for="name_primary">{{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }}
            <span *ngIf="isSecondaryLanguage">({{ primaryLanguage }})</span>
            <sup class="text-red-500">*</sup>
          </label>
          <input 
            pInputText 
            id="name_primary"
            formControlName="name_primary_language" 
            class="w-full mt-2"
            data-testid="product-name-primary-input" />
          <small class="p-error" *ngIf="productForm.get('name_primary_language')?.invalid && productForm.get('name_primary_language')?.touched">
            {{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }} is required
          </small>
        </div>

        <!-- Product Name (Secondary Language) -->
        <div class="col-12 md:col-6" *ngIf="isSecondaryLanguage">
          <label for="name_secondary">{{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }}
            <span>({{ secondaryLanguage }})</span>
          </label>
          <input 
            pInputText 
            id="name_secondary"
            formControlName="name_secondary_language" 
            class="w-full mt-2"
            dir="rtl"
            data-testid="product-name-secondary-input" />
          <small class="p-error" *ngIf="productForm.get('name_secondary_language')?.invalid && productForm.get('name_secondary_language')?.touched">
            {{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }} is required
          </small>
        </div>

        <!-- Description (Primary Language) -->
        <div class="col-12 md:col-6">
          <label for="desc_primary">{{ 'RETAIL_BILLING.PRODUCTS.DESCRIPTION' | translate }}
            <span *ngIf="isSecondaryLanguage">({{ primaryLanguage }})</span>
          </label>
          <textarea 
            pInputTextarea 
            id="desc_primary"
            formControlName="description_primary_language" 
            rows="3"
            class="w-full mt-2"
            data-testid="product-description-primary-input">
          </textarea>
        </div>

        <!-- Description (Secondary Language) -->
        <div class="col-12 md:col-6" *ngIf="isSecondaryLanguage">
          <label for="desc_secondary">{{ 'RETAIL_BILLING.PRODUCTS.DESCRIPTION' | translate }}
            <span>({{ secondaryLanguage }})</span>
          </label>
          <textarea 
            pInputTextarea 
            id="desc_secondary"
            formControlName="description_secondary_language" 
            rows="3"
            class="w-full mt-2"
            dir="rtl"
            data-testid="product-description-secondary-input">
          </textarea>
        </div>

         <!-- Tax Group (only for tenant level) -->
         <div class="col-12" data-testid="code-container">
          <label for="tax_group">{{ 'RETAIL_BILLING.PRODUCTS.CODE' | translate }} <sup class="p-error">*</sup></label>
          <input  
            pInputText 
            id="code"
            formControlName="code"
            class="w-full mt-2"
            data-testid="product-code-input" />
          <small class="p-error" *ngIf="productForm.get('code')?.invalid && productForm.get('code')?.touched">
            {{ 'RETAIL_BILLING.PRODUCTS.CODE' | translate }} is required
          </small>
        </div>

        <!-- Tax Group (only for tenant level) -->
        <div class="col-12" *ngIf="isLevelTenant()" data-testid="tax-group-container">
          <label for="tax_group">{{ 'ADMIN.SERVICES.TAX_GROUP' | translate }} <sup class="p-error">*</sup></label>
          <p-select
            id="tax_group"
            formControlName="tax_group_id"
            [options]="taxGroupOptions"
            placeholder="{{ 'ADMIN.SERVICES.SELECT' | translate }}"
            optionLabel="name_primary_language"
            filterBy="name_primary_language"
            styleClass="w-full text-sm mt-2"
            data-testid="tax-group-dropdown">
            <ng-template pTemplate="selectedItem">
              <div class="text-sm" *ngIf="productForm.get('tax_group_id')?.value">
                {{ productForm.get('tax_group_id')?.value?.name_primary_language }}
              </div>
            </ng-template>
            <ng-template let-taxGroup pTemplate="item">
              <div class="text-sm">{{ taxGroup.name_primary_language }}</div>
            </ng-template>
          </p-select>
          <small class="p-error" *ngIf="productForm.get('tax_group_id')?.invalid && productForm.get('tax_group_id')?.touched">
            {{ 'ADMIN.SERVICES.TAX_GROUP' | translate }} is required
          </small>
        </div>

       

        <!-- Image Upload -->
        <div class="col-12">
          <label>{{ 'RETAIL_BILLING.PRODUCTS.IMAGE' | translate }}</label>
          <div class="mt-2">
            <div class="border-2 border-dashed border-300 border-round p-4 text-center" 
                 style="min-height: 200px; position: relative;">
              
              <!-- Show uploaded image if exists -->
              <div *ngIf="currentImageUrl.value" class="mb-3">
                <img [src]="currentImageUrl.value" alt="Product image" class="max-w-full max-h-8rem border-round" />
                <div class="mt-2">
                  <p-button 
                    icon="pi pi-times" 
                    [text]="true" 
                    [rounded]="true" 
                    severity="danger"
                    (onClick)="removeImage()"
                    aria-label="remove-image-button">
                  </p-button>
                </div>
              </div>

              <!-- File drop zone -->
              <div *ngIf="!currentImageUrl.value" class="mx-auto">
                <ngx-file-drop 
                  class="opacity-0 absolute w-full h-full top-0 left-0 z-4 cursor-pointer" 
                  [accept]="'.png,.jpg,.jpeg,.gif'" 
                  (onFileDrop)="handleFileSelect($event)" 
                  aria-label="file-drop-zone">
                  <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
                    <div (click)="openFileSelector()" class="w-full h-full absolute left-0 top-0" aria-label="file-drop-zone-click"></div>
                  </ng-template>
                </ngx-file-drop>

                <i class="fa fa-upload text-600 text-4xl"></i>
                <p class="text-sm font-bold line-height-4 my-2">
                  {{ 'RETAIL_BILLING.COMMON.DRAG_HERE' | translate }}
                  <span class="text-primary block">{{ 'RETAIL_BILLING.COMMON.BROWSE_FILE' | translate }}</span>
                </p>
                <p class="text-xs mb-0 text-500">
                  {{ 'RETAIL_BILLING.COMMON.ACCEPTED_FILE_TYPE' | translate }}: .Jpeg, .PNG, .JPG, .Gif
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Attribute Schema Configuration Section -->
        <div class="col-12">
          <div class="mb-4">
            <!-- Section Header -->
            <div class="flex align-items-center gap-2 mb-4">
              <i class="pi pi-cog text-primary text-xl"></i>
              <h4 class="text-900 font-semibold m-0">{{ 'RETAIL_BILLING.PRODUCTS.ATTRIBUTE_CONFIGURATION' | translate }}</h4>
              <div class="ml-auto">
                <p-button 
                  label="Add Attribute"
                  icon="pi pi-plus"
                  size="small"
                  (onClick)="addNewAttribute()"
                  aria-label="add-new-attribute-button">
                </p-button>
              </div>
            </div>
            
            <!-- Attributes Configuration List -->
            <div *ngIf="attributeSchemas.length > 0" class="space-y-3">
              <div 
                *ngFor="let attr of attributeSchemas; let i = index; trackBy: trackByAttributeIndex" 
                class="surface-card border-1 surface-border border-round-lg p-4">
                
                <div class="grid">
                  <!-- Attribute Name -->
                  <div class="col-12 md:col-3">
                    <label [for]="'attr_name_' + i">Attribute Name <sup class="text-red-500">*</sup></label>
                    <input 
                      pInputText 
                      [id]="'attr_name_' + i"
                      [(ngModel)]="attr.name"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="e.g., size, color"
                      class="w-full mt-2"
                      data-testid="attribute-name-input" />
                  </div>

                  <!-- Attribute Type -->
                  <div class="col-12 md:col-3">
                    <label [for]="'attr_type_' + i">Type <sup class="text-red-500">*</sup></label>
                    <p-select
                      [id]="'attr_type_' + i"
                      [(ngModel)]="attr.type"
                      [ngModelOptions]="{standalone: true}"
                      [options]="ATTRIBUTE_TYPES"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select type"
                      class="w-full mt-2"
                      (onChange)="onAttributeTypeChange(attr, i)"
                      data-testid="attribute-type-dropdown">
                    </p-select>
                  </div>

                  <!-- Required Toggle -->
                  <div class="col-12 md:col-2">
                    <label [for]="'attr_required_' + i">Required</label>
                    <div class="mt-2">
                      <p-toggleButton
                        [id]="'attr_required_' + i"
                        [(ngModel)]="attr.required"
                        [ngModelOptions]="{standalone: true}"
                        onLabel="Yes"
                        offLabel="No"
                        data-testid="attribute-required-toggle">
                      </p-toggleButton>
                    </div>
                  </div>

                  <!-- Display Order -->
                  <div class="col-12 md:col-2">
                    <label [for]="'attr_order_' + i">Order</label>
                    <input 
                      pInputText 
                      [id]="'attr_order_' + i"
                      [(ngModel)]="attr.display_order"
                      [ngModelOptions]="{standalone: true}"
                      type="number"
                      min="1"
                      placeholder="1"
                      class="w-full mt-2"
                      data-testid="attribute-order-input" />
                  </div>

                  <!-- Remove Button -->
                  <div class="col-12 md:col-2">
                    <label>&nbsp;</label>
                    <div class="mt-2">
                      <p-button 
                        icon="pi pi-trash"
                        severity="danger"
                        [text]="true"
                        size="small"
                        (onClick)="removeAttribute(i)"
                        data-testid="remove-attribute-button">
                      </p-button>
                    </div>
                  </div>

                  <!-- Allowed Values for Enum Type -->
                  <div class="col-12" *ngIf="attr.type.toLowerCase() === 'enum'">
                    <label [for]="'attr_values_' + i">Allowed Values <sup class="text-red-500">*</sup></label>
                    <div class="mt-2">
                      <p-chips
                        [id]="'attr_values_' + i"
                        [(ngModel)]="attr.allowed_values"
                        [ngModelOptions]="{standalone: true}"
                        placeholder="Type value and press Enter"
                        class="w-full"
                        data-testid="attribute-values-chips">
                      </p-chips>
                      <small class="text-500 block mt-1">
                        <i class="pi pi-info-circle mr-1"></i>
                        Type each value and press Enter to add. Click the × to remove values.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div *ngIf="attributeSchemas.length === 0" class="text-center py-8">
              <i class="pi pi-cog text-400 text-6xl mb-3"></i>
              <h5 class="text-600 mb-2">No attributes configured</h5>
              <p class="text-500 mb-4">Add attributes to define product characteristics like size, color, weight, etc.</p>
              <p-button 
                label="Add First Attribute"
                icon="pi pi-plus"
                (onClick)="addNewAttribute()"
                aria-label="add-first-attribute-button">
              </p-button>
            </div>
          </div>
        </div>
      </form>

      <ng-template pTemplate="footer">
        <div class="flex justify-content-end gap-2">
          <p-button 
            [label]="'RETAIL_BILLING.COMMON.CANCEL' | translate"
            icon="pi pi-times"
            [text]="true"
            (onClick)="onCancel.emit()"
            aria-label="cancel-button">
          </p-button>
          <p-button 
            [label]="'RETAIL_BILLING.COMMON.SAVE' | translate"
            icon="pi pi-check"
            [loading]="saving"
            [disabled]="productForm.invalid"
            (onClick)="saveProduct()"
            aria-label="save-product-button">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductFormComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() product: ProductResponse | null = null;
  @Input() tenantId?: number;
  @Input() companyLevel: boolean = true;
  @Input() primaryLanguage!: string;
  @Input() secondaryLanguage!: string;
  @Input() isSecondaryLanguage = false;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<ProductResponse>();
  @Output() onCancel = new EventEmitter<void>();

  productForm: FormGroup;
  categoryOptions: any[] = [];
  brandOptions: any[] = [];
  taxGroupOptions: any[] = [];
  saving = false;
  currentImageUrl = new BehaviorSubject<string | null>(null);
  noTax: any;

  // Attribute Schema Configuration
  attributeSchemas: AttributeSchema[] = [];
  
  // Available attribute types
  readonly ATTRIBUTE_TYPES = [
    { label: 'Dropdown (Enum)', value: 'Enum' },
    { label: 'Toggle (Boolean)', value: 'Boolean' },
    { label: 'Text (String)', value: 'String' },
    { label: 'Number', value: 'Number' },
    { label: 'Date', value: 'Date' }
  ];
  refCropper?: DynamicDialogRef;

  get isEditMode(): boolean {
    return !!this.product;
  }

  get ownershipLevel() {
    return OwnershipLevel;
  }

  isLevelTenant(): boolean {
    return this.productForm?.get('ownership_level')?.value === OwnershipLevel.Tenant;
  }

  selectLevel(level: OwnershipLevel) {
    this.productForm.get('ownership_level')?.setValue(level);
    
    // Reset tax group when level changes
    this.productForm.get('tax_group_id')?.setValue(null);
    
    // Load tax groups for tenant level
    if (level === OwnershipLevel.Tenant && this.tenantId) {
      this.loadTaxGroups();
    } else {
      this.taxGroupOptions = [];
    }
    
    // Update form validation
    this.updateFormValidation();
  }

  constructor(
    private fb: FormBuilder,
    private productService: ProductService,
    private categoryService: ProductCategoryService,
    private brandService: BrandService,
    private taxGroupService: TaxGroupService,
    private toastService: ToastMessageService,
    private translate: TranslateService,
    private dialogService: DialogService,
    private change: ChangeDetectorRef
  ) {
    this.productForm = this.fb.group({
      name_primary_language: ['', Validators.required],
      name_secondary_language: [''],
      description_primary_language: ['', Validators.required],
      description_secondary_language: [''],
      category_id: [null, Validators.required],
      brand_id: [null, Validators.required],
      is_active: [true],
      is_digital: [false],
      ownership_level: [OwnershipLevel.Company, Validators.required],
      image_url: [''],
      attribute_schema: [null],
      code: [''],
      tax_group_id: [null]
    }); 
  }

  ngOnInit() {
    this.setupNoTax();
    this.loadFormData();
    this.updateFormValidation();
  }

  ngOnChanges() {
    if (this.visible && this.product) {
      this.populateForm();
    } else if (this.visible && !this.product) {
      this.resetForm();
    }
    
    // Load tax groups if needed
    if (this.visible && this.tenantId && this.isLevelTenant()) {
      this.loadTaxGroups();
    }
    
    this.updateFormValidation();
  }

  setupNoTax() {
    this.translate.get('ADMIN.SUBSCRIPTION.NOTAX').subscribe((translation) => {
      this.noTax = {
        id: 0,
        name_primary_language: translation,
        name_secondary_language: translation,
      };
    });
  }

  updateFormValidation() {
    if (this.productForm) {
      // const nameSecondaryControl = this.productForm.get('name_secondary_language');
      const taxGroupControl = this.productForm.get('tax_group_id');
      
      // if (this.isSecondaryLanguage) {
      //   nameSecondaryControl?.setValidators([Validators.required]);
      // } else {
      //   nameSecondaryControl?.clearValidators();
      // }
      // nameSecondaryControl?.updateValueAndValidity();

      // Update tax group validation based on level
      if (this.isLevelTenant()) {
        taxGroupControl?.setValidators([Validators.required]);
      } else {
        taxGroupControl?.clearValidators();
      }
      taxGroupControl?.updateValueAndValidity();
    }
  }

  loadFormData() {
    // Load categories
    this.categoryService.getCompanyCategories().subscribe({
      next: (response) => {
        this.categoryOptions = response.items?.map(cat => ({
          name: cat.name_primary_language,
          value: cat.id
        })) || [];
      }
    });

    // Load brands
    this.brandService.getCompanyBrands().subscribe({
      next: (response) => {
        this.brandOptions = response.items?.map(brand => ({
          name: brand.name_primary_language,
          value: brand.id
        })) || [];
      }
    });

    // Load tax groups if tenant level
    if (this.tenantId && this.isLevelTenant()) {
      this.loadTaxGroups();
    }
  }

  loadTaxGroups() {
    if (!this.tenantId) return;
    
    this.taxGroupService.getTaxGroups(this.tenantId).subscribe({
      next: (response) => {
        this.taxGroupOptions = response.items || [];
        if (this.noTax) {
          this.taxGroupOptions.unshift(this.noTax);
        }
      },
      error: (error) => {
        console.error('Error loading tax groups:', error);
      }
    });
  }

  populateForm() {
    if (this.product) {
      this.productForm.patchValue({
        name_primary_language: this.product.name_primary_language || '',
        name_secondary_language: this.product.name_secondary_language || '',
        description_primary_language: this.product.description_primary_language || '',
        description_secondary_language: this.product.description_secondary_language || '',
        category_id: this.product.category_id,
        brand_id: this.product.brand_id,
        is_active: this.product.is_active,
        is_digital: this.product.is_digital,
        ownership_level: this.companyLevel ? OwnershipLevel.Company : OwnershipLevel.Tenant,
        image_url: this.product.image_url,
        tax_group_id: this.product.tax_group_id || null,
        code: this.product.code || ''
      });
      this.currentImageUrl.next(this.product.image_url || null);

      // Populate attribute schema if it exists
      if (this.product.attribute_schema && Array.isArray(this.product.attribute_schema)) {
        this.attributeSchemas = this.product.attribute_schema.map((attr: any) => ({
          name: attr.name || '',
          type: attr.type,
          required: attr.required || false,
          allowed_values: attr.allowed_values || [],
          label: attr.label || '',
          display_order: attr.display_order || 1
        }));
      }
    }
  }

  resetForm() {
    this.productForm.reset({
      status: true,
      ownership_level: this.companyLevel ? OwnershipLevel.Company : OwnershipLevel.Tenant
    });
    this.currentImageUrl.next(null);
    
    // Reset attribute schemas
    this.attributeSchemas = [];
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    if (files && files.length > 0) {
      const file = files[0];
      if (file.fileEntry.isFile) {
        const fileEntry = file.fileEntry as FileSystemFileEntry;
        fileEntry.file((uploadedFile: File) => {
          this.processFile(uploadedFile);
        });
      }
    }
  }

  showCropper(imageFile: any) {
    const header: string = 'common.IMAGE_CROP.CLASS_IMAGE';
    this.refCropper = this.dialogService.open(ImageCropDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      width: '70%',
      contentStyle: { 'max-height': '500px', 'max-width': '800px' },
      data: { imageFile },
    });
    this.refCropper.onClose.subscribe((croppedImage: any) => {
      if (croppedImage) {
        this.currentImageUrl.next(croppedImage.image);
        this.productForm.patchValue({ image_url: croppedImage.image });
        this.change.detectChanges();
      }
    });
  }

  processFile(file: File) {
    // Create a preview URL for the image
    const reader = new FileReader();
    reader.onload = (e) => {
      this.currentImageUrl.next(e.target?.result as string);
      this.productForm.patchValue({ image_url: this.currentImageUrl.value });
      this.change.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removeImage() { 
    this.currentImageUrl.next(null);
    this.productForm.patchValue({ image_url: '' });
  }



  saveProduct() {
    if (this.productForm.valid) {
      this.saving = true;
      const formData = this.productForm.value;

      // Convert attribute schema to API format
      const typeMapping: Record<string, number> = {
        'string': 0,
        'number': 1,
        'boolean': 2,
        'enum': 3,
        'date': 4
      };

      const attributeSchemaForAPI = this.attributeSchemas.map(attr => ({
        name: attr.name,
        type: typeMapping[attr.type] || 0,
        required: attr.required,
        allowed_values: attr.allowed_values || [],
        label: attr.label || attr.name,
        display_order: attr.display_order
      }));

      // Handle tax group id: pass 0 if not tenant level, otherwise use selected value
      const taxGroupId = this.isLevelTenant() 
        ? (formData.tax_group_id?.id !== undefined ? formData.tax_group_id.id : formData.tax_group_id) 
        : 0;

      const request: CreateProductRequest | UpdateProductRequest = {
        name_primary_language: formData.name_primary_language,
        name_secondary_language: formData.name_secondary_language,
        description_primary_language: formData.description_primary_language,
        description_secondary_language: formData.description_secondary_language,
        category_id: formData.category_id,
        brand_id: formData.brand_id,
        is_active: formData.is_active || true ,
        is_digital: formData.is_digital || false,
        image_url: formData.image_url.replace('data:image/png;base64,', '').replace('data:image/jpg;base64,', '').replace('data:image/jpeg;base64,', '').replace('data:image/gif;base64,', ''),
        attribute_schema: attributeSchemaForAPI,
        tax_group_id: taxGroupId,
        code: formData.code || ''
      };

      const saveOperation = this.isEditMode
        ? this.companyLevel
          ? this.productService.updateCompanyProduct(this.product!.id, request as UpdateProductRequest)
          : this.productService.updateTenantProduct(this.tenantId!, this.product!.id, request as UpdateProductRequest)
        : this.companyLevel
          ? this.productService.createCompanyProduct(request as CreateProductRequest)
          : this.productService.createTenantProduct(this.tenantId!, request as CreateProductRequest);

      saveOperation.subscribe({
        next: (response) => {
          this.saving = false;
          // Create a mock product response for the event
          const savedProduct: ProductResponse = {
            ...this.product,
            id: this.product?.id || 0,
            ...request,
            created_at: this.product?.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString(),
            created_by: 1,
            updated_by: 1,
            company_id: 1
          } as ProductResponse;
          
          this.onSave.emit(savedProduct);
          this.visibleChange.emit(false);
        },
        error: (error) => {
          this.saving = false;
          this.toastService.showErrorMessage(this.translate.instant('RETAIL_BILLING.COMMON.ERROR'));
        }
      });
    }
  }

  // Attribute Schema Management Methods
  addNewAttribute() {
    const newAttribute: AttributeSchema = {
      name: '',
      type: 'string',
      required: false,
      allowed_values: [],
      label: '',
      display_order: this.attributeSchemas.length + 1
    };
    this.attributeSchemas.push(newAttribute);
  }

  removeAttribute(index: number) {
    this.attributeSchemas.splice(index, 1);
    // Update display orders
    this.attributeSchemas.forEach((attr, i) => {
      attr.display_order = i + 1;
    });
  }

  onAttributeTypeChange(attr: AttributeSchema, index: number) {
    // Initialize allowed_values for enum type
    if (attr.type.toLowerCase() === 'enum' && (!attr.allowed_values || attr.allowed_values.length === 0)) {
      attr.allowed_values = [];
    } else if (attr.type.toLowerCase() !== 'enum') {
      attr.allowed_values = [];
    }
  }

  trackByAttributeIndex(index: number): number {
    return index;
  }


}